import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { IMenuItem, NavigationService } from './navigation.service';
import { UserPermissionsService } from './user-permissions.service';
import { FeatureFlagModel } from '../../views/settings/feature-flag/models/featureFlag.model';
import { FeatureFlagService } from '../../views/settings/feature-flag/services/feature-flag.service';

export interface MenuFilterOptions {
  excludeNames?: string[];
  featureFlags?: FeatureFlagModel[]; // kept for backward compat, no longer needed
}

@Injectable({
  providedIn: 'root'
})
export class MenuFilterService {
  constructor(
    private navService: NavigationService,
    private userPermissionsService: UserPermissionsService,
    private featureFlagService: FeatureFlagService
  ) {}

  /**
   * Returns an observable of filtered and processed menu items based on permissions and feature flags.
   * Waits internally for feature flags to be available before emitting.
   */
  getFilteredMenuItems(options: MenuFilterOptions = {}): Observable<IMenuItem[]> {
    return combineLatest([
      this.navService.menuItems$,
      this.featureFlagService.currentFeatureFlags.pipe(
        filter(flags => flags !== null && flags !== undefined)
      )
    ]).pipe(
      map(([menuItems, featureFlags]: [IMenuItem[], FeatureFlagModel[]]) => {
        // Filter out excluded items by name
        let filteredMenuItems = menuItems;
        if (options.excludeNames && options.excludeNames.length > 0) {
          filteredMenuItems = menuItems.filter(item => !options.excludeNames.includes(item.name));
        }

        // Process permissions and feature flags
        return filteredMenuItems.map(item => {
          const processedItem = { ...item };

          // Handle permission-based disabling
          if (processedItem.permission) {
            processedItem.disabled = !this.userPermissionsService.hasAllPermissions(processedItem.permission);
          }

          // Handle feature flag-based disabling — only check if not already disabled by permission
          if (!processedItem.disabled && processedItem.featureFlag) {
            processedItem.disabled = !featureFlags.some(flag => flag.key === processedItem.featureFlag && flag.value === 'true');
          }

          return processedItem;
        });
      })
    );
  }
}
